import { useState, useCallback } from 'react';
import { apiClient } from '../index';
import { ReasonBreakdown } from '../../../utils/unsubscribeReasons';

interface DailyStats {
  date: string;
  count: number;
}

interface SourceBreakdown {
  date: string;
  source: string;
  count: number;
  type: 'subscription' | 'unsubscription';
}

export interface SourceSnapshot {
  snapshotDate: string;
  source: string;
  subscriberCount: number;
  optInCount: number;
  newSubscriptions: number;
  unsubscriptions: number;
}

interface SubscriberAnalytics {
  subscriptions: DailyStats[];
  unsubscriptions: DailyStats[];
  sourceBreakdown: SourceBreakdown[];
  reasonBreakdown: ReasonBreakdown[];
}

interface SnapshotData {
  snapshots: SourceSnapshot[];
}

export const useSubscriberAnalytics = (days: number = 30, startDate?: string, endDate?: string, topicId?: number) => {
  const [data, setData] = useState<SubscriberAnalytics | null>(null);
  const [snapshotData, setSnapshotData] = useState<SourceSnapshot[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      let url = `/api/subscribers/daily-stats?days=${days}`;
      if (startDate && endDate) {
        url = `/api/subscribers/daily-stats?startDate=${startDate}&endDate=${endDate}`;
      }
      if (topicId) {
        url += `&topic_id=${topicId}`;
      }
      const response = await apiClient.get(url);
      setData(response as SubscriberAnalytics);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch analytics');
    } finally {
      setIsLoading(false);
    }
  }, [days, startDate, endDate, topicId]);

  const fetchSnapshotData = useCallback(async () => {
    try {
      let url = `/api/subscribers/source-snapshots?days=${days}`;
      if (startDate && endDate) {
        url = `/api/subscribers/source-snapshots?startDate=${startDate}&endDate=${endDate}`;
      }
      if (topicId) {
        url += `&topic_id=${topicId}`;
      }
      const response = await apiClient.get(url) as SnapshotData;
      setSnapshotData(response.snapshots);
    } catch (err) {
      console.error('Failed to fetch snapshot data:', err);
      setSnapshotData(null);
    }
  }, [days, startDate, endDate, topicId]);

  const downloadAnalyticsCsv = useCallback(async (type: 'subscriptions' | 'unsubscriptions', dayCount: number, startDate?: string, endDate?: string) => {
    let url = `/api/subscribers/analytics-csv?type=${type}`;
    if (startDate && endDate) {
      url += `&startDate=${startDate}&endDate=${endDate}`;
    } else {
      url += `&days=${dayCount}`;
    }
    return apiClient.downloadCsv(url);
  }, []);

  const downloadDailyCsv = useCallback(async (type: 'subscriptions' | 'unsubscriptions', date: string) => {
    return apiClient.downloadCsv(`/api/subscribers/daily-csv?type=${type}&date=${date}`);
  }, []);

  return { data, snapshotData, isLoading, error, fetchData, fetchSnapshotData, downloadAnalyticsCsv, downloadDailyCsv };
};